import { Client, type Room } from "colyseus.js";
import { SESSION_REPLACED_CLOSE_CODE, type DrawingGameCanvasResetEvent, type DrawingGameNotice, type DrawingGameSecret, type DrawingGameSnapshot, type DrawingGameStrokeEvent, type DrawingGameSummary, type DrawingStroke, type EmoteType, type LocalChatMessage, type MacaoNotice, type MacaoSnapshot, type MacaoSuit, type MacaoSummary, type MaintenanceNotice, type MovementInput, type OutfitId, type TypingStatus } from "@aproape/shared";
import type { AvatarSnapshot } from "./game/avatar";

export interface NetworkCallbacks {
  onPlayers: (players: AvatarSnapshot[]) => void;
  onChat: (message: LocalChatMessage) => void;
  onTyping: (status: TypingStatus) => void;
  onMaintenance: (notice: MaintenanceNotice) => void;
  onStatus: (status: "connecting" | "online" | "offline") => void;
  onSession: (id: string) => void;
  onSessionReplaced: () => void;
  onDrawingSessions: (sessions: DrawingGameSummary[]) => void;
  onDrawingState: (state: DrawingGameSnapshot) => void;
  onDrawingSecret: (secret: DrawingGameSecret) => void;
  onDrawingStroke: (event: DrawingGameStrokeEvent) => void;
  onDrawingCanvasReset: (event: DrawingGameCanvasResetEvent) => void;
  onDrawingNotice: (notice: DrawingGameNotice) => void;
  onDrawingGuessResult: (result: { sessionId: string; correct: boolean; text: string }) => void;
  onMacaoSummary: (summary: MacaoSummary) => void;
  onMacaoState: (state: MacaoSnapshot) => void;
  onMacaoNotice: (notice: MacaoNotice) => void;
}

export class MovementSequence {
  private value = 0;
  next(): number { return ++this.value; }
}

export class WorldNetwork {
  private room?: Room;
  private readonly movementSequence = new MovementSequence();
  private reconnecting = false;
  private destroyed = false;
  private away = false;
  private displayNameOverride = "";
  private reconnectionToken = "";
  private reconnectTimer?: number;

  constructor(private readonly token: string, private readonly callbacks: NetworkCallbacks) {}

  async connect(): Promise<void> {
    if (this.destroyed || this.reconnecting) return;
    this.reconnecting = true; this.callbacks.onStatus("connecting");
    try {
      const endpoint = import.meta.env.VITE_REALTIME_URL || `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}`;
      const client = new Client(endpoint);
      let room: Room;
      if (this.reconnectionToken) {
        try { room = await client.reconnect(this.reconnectionToken); }
        catch { this.reconnectionToken = ""; room = await client.joinOrCreate("world", { token: this.token }); }
      } else {
        room = await client.joinOrCreate("world", { token: this.token });
      }
      this.room = room;
      this.reconnectionToken = room.reconnectionToken;
      this.callbacks.onSession(room.sessionId); this.sendPresence(); this.sendDisplayName(); this.callbacks.onStatus("online");
      const sync = () => {
        const players: AvatarSnapshot[] = [];
        room.state.players?.forEach((p: any, id: string) => players.push({ id, userId: p.userId, displayName: p.displayName, avatarType: p.avatarType, avatarParts: { head: p.head, top: p.top, pants: p.pants, shoes: p.shoes }, createdAt: p.createdAt, x: p.x, y: p.y, direction: p.direction, moving: p.moving, away: Boolean(p.away), lounging: Boolean(p.lounging), loungeRotation: Number(p.loungeRotation) || 0, emote: p.emote || "", emoteUntil: Number(p.emoteUntil) || 0 }));
        this.callbacks.onPlayers(players);
      };
      room.onStateChange(sync);
      room.onMessage("local_chat_message", (message: LocalChatMessage) => this.callbacks.onChat(message));
      room.onMessage("typing_status", (status: TypingStatus) => this.callbacks.onTyping(status));
      room.onMessage("maintenance", (notice: MaintenanceNotice) => this.callbacks.onMaintenance(notice));
      room.onMessage("drawing_game_sessions", (sessions: DrawingGameSummary[]) => this.callbacks.onDrawingSessions(sessions));
      room.onMessage("drawing_game_state", (state: DrawingGameSnapshot) => this.callbacks.onDrawingState(state));
      room.onMessage("drawing_game_secret", (secret: DrawingGameSecret) => this.callbacks.onDrawingSecret(secret));
      room.onMessage("drawing_game_stroke", (event: DrawingGameStrokeEvent) => this.callbacks.onDrawingStroke(event));
      room.onMessage("drawing_game_canvas_reset", (event: DrawingGameCanvasResetEvent) => this.callbacks.onDrawingCanvasReset(event));
      room.onMessage("drawing_game_notice", (notice: DrawingGameNotice) => this.callbacks.onDrawingNotice(notice));
      room.onMessage("drawing_game_guess_result", (result: { sessionId: string; correct: boolean; text: string }) => this.callbacks.onDrawingGuessResult(result));
      room.onMessage("macao_summary", (summary: MacaoSummary) => this.callbacks.onMacaoSummary(summary));
      room.onMessage("macao_state", (state: MacaoSnapshot) => this.callbacks.onMacaoState(state));
      room.onMessage("macao_notice", (notice: MacaoNotice) => this.callbacks.onMacaoNotice(notice));
      room.onLeave((code) => {
        this.room = undefined;
        if (code === SESSION_REPLACED_CLOSE_CODE) {
          this.destroyed = true;
          this.reconnectionToken = "";
          clearTimeout(this.reconnectTimer);
          this.callbacks.onSessionReplaced();
          return;
        }
        this.callbacks.onStatus("offline");
        this.scheduleReconnect();
      });
      room.onError(() => { this.callbacks.onStatus("offline"); this.scheduleReconnect(); });
    } catch { this.callbacks.onStatus("offline"); this.scheduleReconnect(); }
    finally { this.reconnecting = false; }
  }

  movement(x: number, y: number): void {
    const payload: MovementInput = { type: "movement_input", x, y, sequence: this.movementSequence.next() };
    this.room?.send("movement_input", payload);
  }
  chat(text: string): void { this.room?.send("local_chat", { type: "local_chat", text, clientMessageId: crypto.randomUUID() }); }
  typing(active: boolean): void { this.room?.send("typing", { type: "typing", active }); }
  emote(emote: EmoteType): void { this.room?.send("emote", { type: "emote", emote }); }
  appearance(outfit: OutfitId): void { this.room?.send("appearance", { type: "appearance", outfit }); }
  displayName(displayName: string): void { this.displayNameOverride = displayName; this.sendDisplayName(); }
  inspectDrawingGame(sessionId: string): void { this.room?.send("drawing_game_inspect", { sessionId }); }
  joinDrawingGame(sessionId: string): void { this.room?.send("drawing_game_join", { sessionId }); }
  startDrawingGame(sessionId: string): void { this.room?.send("drawing_game_start", { sessionId }); }
  watchDrawingGame(sessionId: string): void { this.room?.send("drawing_game_watch", { sessionId }); }
  queueDrawingGame(sessionId: string): void { this.room?.send("drawing_game_queue", { sessionId }); }
  createDrawingGame(): void { this.room?.send("drawing_game_create", {}); }
  leaveDrawingGame(sessionId: string): void { this.room?.send("drawing_game_leave", { sessionId }); }
  replayDrawingGame(sessionId: string): void { this.room?.send("drawing_game_replay", { sessionId }); }
  remainDrawingSpectator(sessionId: string): void { this.room?.send("drawing_game_remain_spectator", { sessionId }); }
  submitDrawingGuess(sessionId: string, text: string): void { this.room?.send("drawing_game_guess", { sessionId, text }); }
  sendDrawingStroke(sessionId: string, stroke: DrawingStroke): void { this.room?.send("drawing_game_stroke", { sessionId, stroke }); }
  clearDrawingCanvas(sessionId: string): void { this.room?.send("drawing_game_clear", { sessionId }); }
  undoDrawingCanvas(sessionId: string): void { this.room?.send("drawing_game_undo", { sessionId }); }
  inspectMacao(): void { this.room?.send("macao_inspect", {}); }
  joinMacao(): void { this.room?.send("macao_join", {}); }
  startMacao(): void { this.room?.send("macao_start", {}); }
  leaveMacao(): void { this.room?.send("macao_leave", {}); }
  playMacaoCard(cardId: string, chosenSuit?: MacaoSuit): void { this.room?.send("macao_play", { cardId, chosenSuit }); }
  drawMacaoCard(): void { this.room?.send("macao_draw", {}); }
  replayMacao(): void { this.room?.send("macao_replay", {}); }
  presence(away: boolean): void { this.away = away; this.sendPresence(); }
  private sendDisplayName(): void { if (this.displayNameOverride) this.room?.send("display_name", { type: "display_name", displayName: this.displayNameOverride }); }
  private sendPresence(): void { this.room?.send("presence", { type: "presence", away: this.away }); }
  ensureConnected(): void { if (!this.room) void this.connect(); }
  private scheduleReconnect(): void { if (this.destroyed) return; clearTimeout(this.reconnectTimer); this.reconnectTimer = window.setTimeout(() => void this.connect(), 1200); }
  destroy(consented: boolean): void { this.destroyed = true; this.reconnectionToken = ""; clearTimeout(this.reconnectTimer); void this.room?.leave(consented); }
}
